// Libs
import classNames from 'classnames/bind';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Stack, Typography } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
// Components, Layouts, Pages
import { BaseInput, Loading } from '~/components/common';
// Others
import { defaultValues, schema } from './helper';
import { RoleEnum } from '~/utils/enum/common';
import { StorageEnum } from '~/utils/enum/base';
import { EMPTY_STRING } from '~/utils/constants/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { changePasswordUser } from '~/thunks/user/userThunk';
import { IBodyChangePassword, IFormChangePassword } from '~/utils/interface/user';
import { adminRouteAbsolute } from '~/utils/routes/roleRoutes/admin';
import { salesAdminRouteAbsolute } from '~/utils/routes/roleRoutes/salesAdmin';
import { salesPersonRouteAbsolute } from '~/utils/routes/roleRoutes/salesPerson';
import { operationsManagerRouteAbsolute } from '~/utils/routes/roleRoutes/operationsManager';
import { warehouseLeadRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseLead';
import { warehouseStaffRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseStaff';
import { foremanRouteAbsolute } from '~/utils/routes/roleRoutes/foreman';
import { crewRouteAbsolute } from '~/utils/routes/roleRoutes/crew';
import { loaderRouteAbsolute } from '~/utils/routes/roleRoutes/loader';
// Styles, images, icons
import styles from './UserChangePasswordTab.module.scss';

type Props = {};

const cx = classNames.bind(styles);

const UserChangePasswordTab = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/users', 'common/base']);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id: userId } = useParams();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<IFormChangePassword>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValues,
  });

  const currentRole: string = JSON.parse(localStorage.getItem(StorageEnum.ROLE)!)[0] ?? EMPTY_STRING;
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const handleSubmitForm = (data: IFormChangePassword) => {
    setIsLoading(true);

    const body: IBodyChangePassword = {
      accountId: userId || EMPTY_STRING,
      currentPassword: data.currentPassword,
      newPassword: data.newPassword,
    };
    dispatch(changePasswordUser(body))
      .unwrap()
      .then((_resp) => {
        handleOnBack();
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleOnBack = () => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(adminRouteAbsolute.users);
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.users);
        break;
      case RoleEnum.SALES_PERSON:
        navigate(salesPersonRouteAbsolute.users);
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(operationsManagerRouteAbsolute.users);
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(warehouseLeadRouteAbsolute.users);
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(warehouseStaffRouteAbsolute.users);
        break;
      case RoleEnum.FOREMAN:
        navigate(foremanRouteAbsolute.users);
        break;
      case RoleEnum.CREW:
        navigate(crewRouteAbsolute.users);
        break;
      case RoleEnum.LOADER:
        navigate(loaderRouteAbsolute.users);
        break;
    }
  };
  //#endregion Handle Function

  return (
    <Box id='userChangePasswordTabComponent' className={cx('container')}>
      <Stack mt={'18px'}>
        <Typography className={cx('textTitle')}>{t('form_change_password_title')}</Typography>
      </Stack>

      <Stack direction={'column'} gap={'12px'}>
        <Controller
          name='currentPassword'
          control={control}
          render={({ field: { value, onChange } }) => (
            <BaseInput
              label={t('form_change_password_current_password_label')}
              value={value}
              onChange={onChange}
              errorMessage={errors?.currentPassword?.message}
              required
            />
          )}
        />

        <Controller
          name='newPassword'
          control={control}
          render={({ field: { value, onChange } }) => (
            <BaseInput
              label={t('form_change_password_new_password_label')}
              value={value}
              onChange={onChange}
              errorMessage={errors?.newPassword?.message}
              required
            />
          )}
        />

        <Controller
          name='confirmNewPassword'
          control={control}
          render={({ field: { value, onChange } }) => (
            <BaseInput
              label={t('form_change_password_confirm_password_label')}
              value={value}
              onChange={onChange}
              errorMessage={errors?.confirmNewPassword?.message}
              required
            />
          )}
        />

        <Stack direction={'row'} justifyContent={'flex-end'} mt={5}>
          <Button variant='contained' onClick={handleSubmit(handleSubmitForm)}>
            {t('common_action_save', { ns: 'common/base' })}
          </Button>
        </Stack>
      </Stack>

      {isLoading && <Loading />}
    </Box>
  );
};

export default UserChangePasswordTab;
